/**
 * capitals.ts — the two civilization CAPITALS and the Grand Circuit campus
 * (2026-09-15). Pure + seed-derived; VERBATIM-MIRRORED in
 * `server/src/lib/capitals.ts` (only the import lines differ — verify:capitals
 * compares the two files with import lines stripped).
 *
 * A capital is a CLUSTER of district sites (each an ordinary city-scale plan)
 * around one capital site that owns the skeleton (boulevards, ring roads,
 * plaza, wall, ports). This file holds the layout constants, the district
 * offsets, the program/name tables and the deterministic placement of the
 * three appended sites (capital A, capital B, the circuit campus). The picker
 * APPENDS after the classic sites and uses no rng, so nothing existing moves.
 */
import { terrainHeightBase, PLATEAU_RADIUS } from './terrain.js';
import { SEA_LEVEL_M } from './constants.js';
import type { SettlementTier } from './settlements.js';

export type Civilization = 'a' | 'b';

/** MIRROR of client biome_settlements.civilizationOf and server npc_sim.civilizationOf
 *  (FNV-1a over `civ|siteSeed`, < 0.5 → 'a'). verify:capitals asserts the three agree. */
export function civilizationOf(siteSeed: number): Civilization {
  let h = 0x811c9dc5;
  const s = `civ|${siteSeed}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff < 0.5 ? 'a' : 'b';
}

/** FNV-1a → [0,1) — the ports/settlement hash family. */
function h01(seed: number, salt: string): number {
  let h = 0x811c9dc5;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff;
}

// ── Layout (capital-local metres / radians) — spec §5 ───────────────────────
/** Civic core disc: the plaza (220 × 180 m) and its four civic pads. */
export const CAPITAL_CORE_R = 450;
/** Every district is a city-scale plan of this fixed radius. */
export const DISTRICT_PLAN_R = 420;
/** The megawall ring. */
export const CAPITAL_WALL_R = 3230;
/** The capital's level disc (wall + 150 m). */
export const CAPITAL_PLATEAU_R = 3380;
/** Ring roads (collector class) in the three radial corridors. */
export const CAPITAL_RING_ROADS: readonly number[] = [490, 1400, 2300];
/** District rings: radius, count, angular phase. Ring 1 at theta0 + k·60°,
 *  rings 2/3 at theta0 + 15° + k·30° — so the six boulevards (theta0 + 30° + k·60°)
 *  fall in a gap of every ring by construction. */
export const CAPITAL_RINGS: ReadonlyArray<{ r: number; count: number; phase: number }> = [
  { r: 950, count: 6, phase: 0 },
  { r: 1850, count: 12, phase: Math.PI / 12 },
  { r: 2750, count: 12, phase: Math.PI / 12 },
];
export const DISTRICTS_PER_CAPITAL = 30;
export const BOULEVARD_COUNT = 6;
/** The stadium's level disc at the circuit campus. */
export const CAMPUS_PLATEAU_R = 500;
/** The campus reservation kept clear of every other site. */
export const CAMPUS_CLEAR_R = 2400;
export const CAPITAL_MIN_APART_M = 15000;
export const CAPITAL_MAX_APART_M = 24000;
/** Base-terrain relief allowed over a capital disc (m) — no giant cut/fill. */
export const CAPITAL_RELIEF_MAX_M = 30;
/** Placement lattice: ±PLACE_SCAN_M around the spawn region at PLACE_STEP_M (spec §4: 250 m).
 *  25,921 points × a short-circuiting clearance test ≈ 40 ms; relief (37 base-terrain
 *  calls ≈ 3 ms) is sampled only for candidates that pass it, nearest-to-origin first. */
const PLACE_SCAN_M = 20000;
const PLACE_STEP_M = 250;
/** terrain.ts PLATEAU_GAP / PLATEAU_RING_MAX (private there) + a placement margin.
 *  Two blend rings that overlap ADD their ramps (verify:plateaus caught the hub
 *  city's ring at 35.3° against capital A's), so a capital keeps its disc a full
 *  ring-width on each side clear of every classic disc — rings never meet. */
const PLATEAU_GAP_M = 80;
const PLATEAU_RING_MAX_M = 480;
const PLACE_MARGIN_M = 60;
const SEA_CLEAR_M = 6;
const CAMPUS_OFFSETS_M: readonly number[] = [0, 1000, -1000, 2000, -2000, 3000, -3000];
/** A district is nudged east by this until its siteSeed hashes to its capital's civ. */
export const DISTRICT_NUDGE_M = 0.01;
export const DISTRICT_NUDGE_TRIES = 12;

export function capitalTheta0(siteSeed: number): number {
  return h01(siteSeed, 'cap-rot') * Math.PI * 2;
}

export interface DistrictOffset { index: number; ring: number; k: number; ang: number; dx: number; dz: number }

/** The 30 district centres relative to the capital centre, ring order. */
export function districtOffsets(theta0: number): DistrictOffset[] {
  const out: DistrictOffset[] = [];
  CAPITAL_RINGS.forEach((ring, ri) => {
    for (let k = 0; k < ring.count; k++) {
      const ang = theta0 + ring.phase + (k / ring.count) * Math.PI * 2;
      out.push({ index: out.length, ring: ri, k, ang, dx: Math.cos(ang) * ring.r, dz: Math.sin(ang) * ring.r });
    }
  });
  return out;
}

/** Boulevard angles: through the ring-1 gaps, and every ring's gaps by construction. */
export function boulevardAngles(theta0: number): number[] {
  return Array.from({ length: BOULEVARD_COUNT }, (_, k) => theta0 + Math.PI / 6 + (k / BOULEVARD_COUNT) * Math.PI * 2);
}
/** Service gates: the other six ring-3 gaps. */
export function serviceGateAngles(theta0: number): number[] {
  return Array.from({ length: BOULEVARD_COUNT }, (_, k) => theta0 + (k / BOULEVARD_COUNT) * Math.PI * 2);
}


// ── Gates and the civic core — spec §5 / §8 (phase 3) ───────────────────────
/** Every gate's exit node sits this far past the wall radius (the skeleton's exits; routes leave from here). */
export const CAPITAL_GATE_EXIT_M = 60;
/** The ports stand OUTSIDE the main gate: this far past the wall, this far beside the boulevard (spaceport left, shuttle right, terminal nearer). */
export const CAPITAL_PORT_OUT_M = 250;
export const CAPITAL_PORT_SIDE_M = 70;
export const CAPITAL_TERMINAL_OUT_M = 190;
export const CAPITAL_TERMINAL_SIDE_M = 24;
/** The civic core: the plaza room (long axis on theta0) and the four civic parcels on its edges. */
export const CAPITAL_PLAZA_HW = 110;
export const CAPITAL_PLAZA_HD = 90;
export const CIVIC_PARCEL_HW = 60;

/** The 12 gate angles in the SKELETON's gate order: 0–5 the boulevard (main-type) gates, 6–11 the service gates. */
export function capitalGateAngles(theta0: number): number[] {
  return [...boulevardAngles(theta0), ...serviceGateAngles(theta0)];
}

/** Smallest absolute angle between two bearings (0..π). */
export function angDiff(a: number, b: number): number {
  let d = Math.abs(a - b) % (Math.PI * 2);
  if (d > Math.PI) d = Math.PI * 2 - d;
  return d;
}

/** The MAIN gate: the boulevard (0–5) whose angle is nearest the bearing (dx, dz) — toward the other capital. */
export function capitalMainGate(theta0: number, dx: number, dz: number): number {
  const bearing = Math.atan2(dz, dx);
  let best = 0, bestD = Infinity;
  boulevardAngles(theta0).forEach((a, k) => { const d = angDiff(a, bearing); if (d < bestD) { bestD = d; best = k; } });
  return best;
}

/** The gate (0–11, skeleton order) nearest a bearing — where a route leaves the capital. */
export function nearestCapitalGate(theta0: number, bearing: number): number {
  let best = 0, bestD = Infinity;
  capitalGateAngles(theta0).forEach((a, k) => { const d = angDiff(a, bearing); if (d < bestD) { bestD = d; best = k; } });
  return best;
}

/** A gate's EXIT point (capital-local): on the gate's angle, CAPITAL_GATE_EXIT_M past the wall. */
export function capitalGateExit(theta0: number, gate: number): { x: number; z: number; ang: number } {
  const a = capitalGateAngles(theta0)[gate];
  const r = CAPITAL_WALL_R + CAPITAL_GATE_EXIT_M;
  return { x: Math.cos(a) * r, z: Math.sin(a) * r, ang: a };
}

export interface CoreRect { cx: number; cz: number; hw: number; hd: number; yaw: number }
/** The plaza room + the four civic parcels (capital-local; u = the plaza's long axis on theta0, v across). */
export function civicCoreLayout(theta0: number): { plaza: CoreRect; parcels: CoreRect[] } {
  const ux = Math.cos(theta0), uz = Math.sin(theta0);
  const vx = -Math.sin(theta0), vz = Math.cos(theta0);
  const du = CAPITAL_PLAZA_HW + 10 + CIVIC_PARCEL_HW, dv = CAPITAL_PLAZA_HD + 10 + CIVIC_PARCEL_HW;
  const parcels: CoreRect[] = [
    { cx: ux * du, cz: uz * du, hw: CIVIC_PARCEL_HW, hd: CIVIC_PARCEL_HW, yaw: theta0 },
    { cx: -ux * du, cz: -uz * du, hw: CIVIC_PARCEL_HW, hd: CIVIC_PARCEL_HW, yaw: theta0 },
    { cx: vx * dv, cz: vz * dv, hw: CIVIC_PARCEL_HW, hd: CIVIC_PARCEL_HW, yaw: theta0 },
    { cx: -vx * dv, cz: -vz * dv, hw: CIVIC_PARCEL_HW, hd: CIVIC_PARCEL_HW, yaw: theta0 },
  ];
  return { plaza: { cx: 0, cz: 0, hw: CAPITAL_PLAZA_HW, hd: CAPITAL_PLAZA_HD, yaw: theta0 }, parcels };
}

/** The four corners of a CoreRect (capital-local, counter-clockwise), flat [x0,z0,…]. */
export function rectCorners(r: CoreRect): number[] {
  const c = Math.cos(r.yaw), s = Math.sin(r.yaw);
  const out: number[] = [];
  for (const [u, v] of [[-r.hw, -r.hd], [r.hw, -r.hd], [r.hw, r.hd], [-r.hw, r.hd]] as const) {
    out.push(r.cx + u * c - v * s, r.cz + u * s + v * c);
  }
  return out;
}


// ── The server's capital: residents by district, the enclosure — spec §9 (phase 4) ──
/** A u32 mix of a salt (an agent id, an event id, a birth seed) — the district pick. */
export function saltHash(salt: bigint | number): number {
  let h = (0x811c9dc5 ^ (typeof salt === 'bigint' ? Number(salt % 0x7fffffffn) : (salt | 0))) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39) >>> 0;
  h ^= h >>> 15;
  return h >>> 0;
}
/** The district (0..count-1) a salt belongs to — homes, jobs and ceremonies of one salt share it. */
export function capitalDistrictOf(count: number, salt: bigint | number): number {
  return count > 0 ? saltHash(salt) % count : 0;
}
/** The capital whose PLATEAU disc holds (x, z), or null. */
export function capitalContaining<T extends SiteLike>(sites: readonly T[], x: number, z: number): T | null {
  for (const s of sites) if (s.tier === 'capital' && Math.hypot(s.centerX - x, s.centerZ - z) <= CAPITAL_PLATEAU_R) return s;
  return null;
}
/** Does the segment a→b cross the circle (c, r)? One end in and one out, or a chord through it. */
export function segmentCrossesCircle(ax: number, az: number, bx: number, bz: number, cx: number, cz: number, r: number): boolean {
  const inA = Math.hypot(ax - cx, az - cz) <= r, inB = Math.hypot(bx - cx, bz - cz) <= r;
  if (inA !== inB) return true;
  if (inA && inB) return false;
  const dx = bx - ax, dz = bz - az;
  const L2 = dx * dx + dz * dz;
  if (L2 === 0) return false;
  const t = Math.max(0, Math.min(1, ((cx - ax) * dx + (cz - az) * dz) / L2));
  return Math.hypot(ax + dx * t - cx, az + dz * t - cz) < r;
}

/** Both ends inside ONE capital's plateau: the trip WALKS whatever its length — the enclosure is one city. */
export function walksInsideCapitalSites<T extends SiteLike & { id: number }>(sites: readonly T[], ax: number, az: number, bx: number, bz: number): boolean {
  const a = capitalContaining(sites, ax, az);
  return !!a && capitalContaining(sites, bx, bz)?.id === a.id;
}
/** A land leg whose straight line crosses a capital's WALL ring goes by ship (the wall is 48–64 m tall). */
export function crossesCapitalWallSites(sites: readonly SiteLike[], ax: number, az: number, bx: number, bz: number): boolean {
  for (const s of sites) if (s.tier === 'capital' && segmentCrossesCircle(ax, az, bx, bz, s.centerX, s.centerZ, CAPITAL_WALL_R)) return true;
  return false;
}

/** The campus FRAME (phase 5): t̂ toward capital A (the stadium straight's direction), n̂ the
 *  perpendicular on capital B's side (the straight stands CAMPUS_STRAIGHT_OFF_M along n̂; the
 *  shuttle pad stands CAMPUS_PAD_M along −n̂). Shared by the circuit, the ports and their mirrors. */
export const CAMPUS_STRAIGHT_OFF_M = 320;
export const CAMPUS_PAD_M = 150;
export const CAMPUS_TERMINAL_M = 60;
export function campusFrame(cx: number, cz: number, ax: number, az: number, bx: number, bz: number): { t: { x: number; z: number }; n: { x: number; z: number } } {
  const dA = Math.hypot(ax - cx, az - cz) || 1;
  const t = { x: (ax - cx) / dA, z: (az - cz) / dA };
  let n = { x: -t.z, z: t.x };
  if ((bx - cx) * n.x + (bz - cz) * n.z < 0) n = { x: -n.x, z: -n.z };
  return { t, n };
}

// ── Programs and names — spec §5 ────────────────────────────────────────────
export type DistrictZoning = 'wards' | 'commerce' | 'industry' | 'gardens' | 'civic' | 'mixed';
export interface DistrictProgram { key: string; name: string; zoning: DistrictZoning }

export const CAPITAL_NAMES: Record<Civilization, string> = { a: 'Aurelian Concourse', b: 'Borean Crownworks' };
export const CORE_NAMES: Record<Civilization, string> = { a: 'Concord Heart', b: 'Grand Gatherhall' };
export const CIRCUIT_NAME = 'The Grand Circuit';

export const DISTRICT_PROGRAMS: Record<Civilization, readonly DistrictProgram[]> = {
  a: [
    { key: 'exchange', name: 'Gilded Exchange', zoning: 'commerce' },
    { key: 'academy', name: 'Luminous Academy', zoning: 'civic' },
    { key: 'wards', name: 'Garden Wards', zoning: 'wards' },
    { key: 'promenade', name: 'Sunward Promenade', zoning: 'mixed' },
    { key: 'festival', name: 'Festival Quarter', zoning: 'mixed' },
    { key: 'craft', name: 'Craft Courts', zoning: 'industry' },
    { key: 'gateway', name: 'Gateway Exchange', zoning: 'commerce' },
    { key: 'provision', name: 'Provision Gardens', zoning: 'gardens' },
    { key: 'belt', name: 'Service and Defense Belt', zoning: 'industry' },
  ],
  b: [
    { key: 'tradeworks', name: 'Tradeworks', zoning: 'commerce' },
    { key: 'crown', name: 'Crown Terraces', zoning: 'civic' },
    { key: 'hearth', name: 'Hearth Wards', zoning: 'wards' },
    { key: 'knowledge', name: 'Knowledge Vaults', zoning: 'civic' },
    { key: 'arena', name: 'Arena and Lantern Ward', zoning: 'mixed' },
    { key: 'forge', name: 'Forge Belt', zoning: 'industry' },
    { key: 'freight', name: 'Freight Crown', zoning: 'industry' },
    { key: 'undercroft', name: 'Undercroft Commons', zoning: 'mixed' },
    { key: 'greenworks', name: 'Greenworks and Wallward', zoning: 'gardens' },
  ],
};

/** Program index per district in ring order (ring 1 k0..5, ring 2 k0..11, ring 3 k0..11).
 *  A totals: exchange 3 · academy 3 · wards 9 · promenade 2 · festival 2 · craft 3 · gateway 2 · provision 3 · belt 3.
 *  B totals: tradeworks 3 · crown 3 · hearth 9 · knowledge 2 · arena 2 · forge 3 · freight 3 · undercroft 2 · greenworks 3. */
export const DISTRICT_ASSIGNMENT: Record<Civilization, readonly number[]> = {
  a: [0, 2, 1, 4, 2, 5,
      2, 6, 0, 2, 1, 3, 2, 5, 4, 2, 7, 1,
      8, 7, 2, 6, 5, 3, 8, 2, 7, 0, 8, 2],
  b: [0, 2, 1, 4, 2, 5,
      2, 6, 0, 2, 1, 3, 2, 5, 4, 2, 8, 1,
      8, 6, 2, 7, 5, 3, 8, 2, 7, 0, 6, 2],
};

export function districtProgram(civ: Civilization, index: number): DistrictProgram {
  const list = DISTRICT_PROGRAMS[civ];
  return list[DISTRICT_ASSIGNMENT[civ][index] % list.length];
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
/** "Garden Wards · III" — the ordinal counts same-program districts in ring order. */
export function districtName(civ: Civilization, index: number): string {
  const assign = DISTRICT_ASSIGNMENT[civ];
  const p = assign[index];
  let nth = 0;
  for (let i = 0; i <= index; i++) if (assign[i] === p) nth++;
  return `${DISTRICT_PROGRAMS[civ][p].name} · ${ROMAN[(nth - 1) % ROMAN.length]}`;
}

// ── Placement — spec §4 ─────────────────────────────────────────────────────
export interface SiteLike { centerX: number; centerZ: number; tier: SettlementTier }
export interface CapitalPlacement { a: [number, number]; b: [number, number]; campus: [number, number] }

/** Base-terrain relief over a disc: centre + rings at 0.35 / 0.7 / 1.0 R, 12 samples each (37 calls). */
function discRelief(seed: string, x: number, z: number, R: number): { relief: number; min: number } {
  let lo = Infinity, hi = -Infinity;
  const push = (px: number, pz: number): void => { const h = terrainHeightBase(seed, px, pz); if (h < lo) lo = h; if (h > hi) hi = h; };
  push(x, z);
  for (const f of [0.35, 0.7, 1.0]) for (let i = 0; i < 12; i++) { const a = (i / 12) * Math.PI * 2; push(x + Math.cos(a) * R * f, z + Math.sin(a) * R * f); }
  return { relief: hi - lo, min: lo };
}

/** True when a disc of radius `r` at (x, z) keeps every existing site's PLATEAU disc
 *  two ring-widths + the plateau gap + margin away (no existing plateau ever
 *  shrinks, and no two blend rings ever overlap). */
function clearOf(existing: readonly SiteLike[], x: number, z: number, r: number): boolean {
  for (const s of existing) {
    const need = r + PLATEAU_RADIUS[s.tier] + 2 * PLATEAU_RING_MAX_M + PLATEAU_GAP_M + PLACE_MARGIN_M;
    const dx = s.centerX - x, dz = s.centerZ - z;
    if (dx * dx + dz * dz < need * need) return false;
  }
  return true;
}

/** The candidate lattice around the spawn region, nearest-to-origin first (ties by x, then z). */
function lattice(): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  for (let x = -PLACE_SCAN_M; x <= PLACE_SCAN_M; x += PLACE_STEP_M) for (let z = -PLACE_SCAN_M; z <= PLACE_SCAN_M; z += PLACE_STEP_M) pts.push([x, z]);
  pts.sort((p, q) => (p[0] * p[0] + p[1] * p[1]) - (q[0] * q[0] + q[1] * q[1]) || p[0] - q[0] || p[1] - q[1]);
  return pts;
}

/**
 * Deterministic placement of capital A (civ a), capital B (civ b) and the
 * circuit campus, against the ALREADY-PLACED classic sites. Relief is only
 * sampled for candidates that pass the cheap tests, nearest-to-origin first,
 * and the search stops at the first legal one (≈ tens of base-terrain discs).
 * Throws with a diagnostic when a seed has no legal solution — a bake error,
 * never a silent shrink.
 */
export function placeCapitals(
  seed: string, existing: readonly SiteLike[],
  seedFor: (x: number, z: number, id: number) => number, firstId: number,
): CapitalPlacement {
  const pts = lattice();
  const legal = (x: number, z: number, want: Civilization, id: number): boolean => {
    if (!clearOf(existing, x, z, CAPITAL_PLATEAU_R)) return false;
    if (civilizationOf(seedFor(x, z, id)) !== want) return false;
    const st = discRelief(seed, x, z, CAPITAL_PLATEAU_R);
    return st.relief <= CAPITAL_RELIEF_MAX_M && st.min >= SEA_LEVEL_M + SEA_CLEAR_M;
  };
  let a: [number, number] | null = null;
  for (const [x, z] of pts) if (legal(x, z, 'a', firstId)) { a = [x, z]; break; }
  if (!a) throw new Error(`placeCapitals(${seed}): no legal site for capital A within ±${PLACE_SCAN_M} m`);
  let b: [number, number] | null = null;
  for (const [x, z] of pts) {
    const d = Math.hypot(x - a[0], z - a[1]);
    if (d < CAPITAL_MIN_APART_M || d > CAPITAL_MAX_APART_M) continue;
    if (legal(x, z, 'b', firstId + 1)) { b = [x, z]; break; }
  }
  if (!b) throw new Error(`placeCapitals(${seed}): no legal site for capital B ${CAPITAL_MIN_APART_M}–${CAPITAL_MAX_APART_M} m from A`);
  const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const mx = (a[0] + b[0]) / 2, mz = (a[1] + b[1]) / 2;
  const nx = -(b[1] - a[1]) / d, nz = (b[0] - a[0]) / d;
  const minToCapital = CAPITAL_PLATEAU_R + CAMPUS_CLEAR_R + 200;
  let campus: [number, number] | null = null;
  for (const off of CAMPUS_OFFSETS_M) {
    const x = mx + nx * off, z = mz + nz * off;
    if (!clearOf(existing, x, z, CAMPUS_CLEAR_R)) continue;
    if (Math.hypot(x - a[0], z - a[1]) < minToCapital || Math.hypot(x - b[0], z - b[1]) < minToCapital) continue;
    const st = discRelief(seed, x, z, CAMPUS_PLATEAU_R);
    if (st.relief > CAPITAL_RELIEF_MAX_M || st.min < SEA_LEVEL_M + SEA_CLEAR_M) continue;
    campus = [x, z];
    break;
  }
  if (!campus) throw new Error(`placeCapitals(${seed}): no legal campus on the A–B midline`);
  return { a, b, campus };
}
